<template>
  <div class="file-preview-container">
    <!-- Loading state -->
    <div v-if="loading" class="preview-loading">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando preview...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="preview-error">
      <feather-icon icon="AlertCircleIcon" size="24" />
      <div class="error-message">{{ error }}</div>
    </div>

    <!-- Preview content -->
    <div v-else class="preview-content">
      <!-- Image preview -->
      <div v-if="isImage" class="image-preview">
        <img :src="previewUrl" :alt="file.name" @error="handleImageError" />
      </div>

      <!-- PDF preview -->
      <div v-else-if="isPDF" class="pdf-preview">
        <iframe :src="previewUrl" frameborder="0"></iframe>
      </div>

      <!-- Text preview -->
      <div v-else-if="isText" class="text-preview">
        <pre>{{ textContent }}</pre>
      </div>

      <!-- Video preview -->
      <div v-else-if="isVideo" class="video-preview">
        <video controls>
          <source :src="previewUrl" :type="file.mime_type">
          Seu navegador não suporta o elemento de vídeo.
        </video>
      </div>

      <!-- Audio preview -->
      <div v-else-if="isAudio" class="audio-preview">
        <audio controls>
          <source :src="previewUrl" :type="file.mime_type">
          Seu navegador não suporta o elemento de áudio.
        </audio>
      </div>

      <!-- Unsupported file type -->
      <div v-else class="unsupported-preview">
        <feather-icon icon="FileIcon" size="48" />
        <div class="unsupported-message">
          Preview não disponível para este tipo de arquivo
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FilePreview',
  props: {
    file: {
      type: Object,
      required: true
    },
    previewUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      textContent: null
    }
  },
  computed: {
    isImage() {
      const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      return imageTypes.includes(this.file.mime_type);
    },
    isPDF() {
      return this.file.mime_type === 'application/pdf';
    },
    isText() {
      const textTypes = [
        'text/plain',
        'text/html',
        'text/css',
        'text/javascript',
        'application/json',
        'application/xml'
      ];
      return textTypes.includes(this.file.mime_type);
    },
    isVideo() {
      const videoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
      return videoTypes.includes(this.file.mime_type);
    },
    isAudio() {
      const audioTypes = ['audio/mpeg', 'audio/ogg', 'audio/wav'];
      return audioTypes.includes(this.file.mime_type);
    }
  },
  watch: {
    previewUrl: {
      immediate: true,
      handler(newUrl) {
        this.loadPreview(newUrl);
      }
    }
  },
  methods: {
    async loadPreview(url) {
      this.loading = true;
      this.error = null;
      this.textContent = null;

      try {
        if (this.isText) {
          const response = await fetch(url);
          if (!response.ok) throw new Error('Falha ao carregar o conteúdo do arquivo');
          this.textContent = await response.text();
        }
      } catch (err) {
        console.error('Erro ao carregar preview:', err);
        this.error = 'Não foi possível carregar o preview do arquivo';
      } finally {
        this.loading = false;
      }
    },
    handleImageError() {
      this.error = 'Não foi possível carregar a imagem';
    }
  }
}
</script>

<style scoped>
.file-preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.preview-loading,
.preview-error,
.unsupported-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  text-align: center;
}

.error-message,
.unsupported-message {
  color: #6c757d;
  font-size: 0.9rem;
}

.preview-content {
  width: 100%;
  height: 100%;
}

.image-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.image-preview img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.pdf-preview {
  width: 100%;
  height: 70vh;
  min-height: 500px;
}

.pdf-preview iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.text-preview {
  padding: 1rem;
  overflow: auto;
  max-height: 70vh;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.text-preview pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

.video-preview,
.audio-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.video-preview video {
  max-width: 100%;
  max-height: 70vh;
}

.audio-preview audio {
  width: 100%;
  max-width: 500px;
}
</style>
